/**
 * Proctoring recorders: record MediaStreams to the backend as ordered .webm chunks (the backend
 * re-encodes to .mp4 — audio to .wav — on stop). Chunks upload through a single promise chain — one in flight at a time —
 * so the appended file stays in order. Used by MediaProvider.
 *
 * The SCREEN recorder is special: it captures the entire shared screen (video only — the candidate's
 * webcam is never in it) and mixes ALL audio sources into the same file via a Web Audio mixer:
 *   • system audio  — the screen-share's own audio track (if the candidate ticked "Share audio")
 *   • mic audio     — the candidate's microphone (attached as soon as the mic is live)
 *   • AI voice      — Wiza's spoken lines (see createAiVoiceBus): the browser's SpeechSynthesis can't
 *                     be captured, so the page fetches a server-TTS WAV of each line and plays it into
 *                     this bus → it lands in the recording at full volume (live playback is unchanged).
 * So one recording = screen + system audio + mic + AI voice. The audio-only recorder mixes mic + AI.
 * Best-effort throughout.
 */
import { uploadRecordingChunk, stopRecording } from '../../ai-interview/utils/aiInterviewApi';

const MIME_AV = ['video/webm;codecs=vp8,opus', 'video/webm;codecs=vp9,opus', 'video/webm'];
const MIME_VIDEO = ['video/webm;codecs=vp8', 'video/webm'];
const MIME_AUDIO = ['audio/webm;codecs=opus', 'audio/webm'];

function pickMime(cands) {
  const can = typeof MediaRecorder !== 'undefined' && MediaRecorder.isTypeSupported;
  for (let i = 0; i < cands.length; i += 1) {
    if (can && MediaRecorder.isTypeSupported(cands[i])) return cands[i];
  }
  return '';
}

const NOOP = { stop: async () => {} };

// Core: record a ready MediaStream → ordered chunk upload → finalize. `onCleanup` tears down any
// audio graph built for this recording.
function record({ stream, mime, kind, ids, timesliceMs, onCleanup }) {
  const { companyId, sessionId, candidateId } = ids || {};
  if (!stream || !sessionId || !candidateId || typeof MediaRecorder === 'undefined') {
    if (onCleanup) onCleanup();
    return NOOP;
  }

  let rec;
  try {
    rec = mime ? new MediaRecorder(stream, { mimeType: mime }) : new MediaRecorder(stream);
  } catch {
    if (onCleanup) onCleanup();
    return NOOP;
  }

  const recordingId = `${kind}_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
  const startedAt = new Date().toISOString();
  let seq = 0;
  let bytes = 0;
  let chunks = 0;
  let chain = Promise.resolve();   // sequential upload queue (keeps chunk order)
  let stopped = false;

  const enqueue = (blob) => {
    const mySeq = seq;
    seq += 1;
    chain = chain.then(async () => {
      try {
        await uploadRecordingChunk(companyId, sessionId, candidateId, kind, recordingId, mySeq, blob);
        bytes += blob.size;
        chunks += 1;
      } catch {
        /* best-effort proctoring — skip a failed chunk, keep recording */
      }
    });
  };

  rec.ondataavailable = (e) => { if (e.data && e.data.size) enqueue(e.data); };

  try {
    rec.start(timesliceMs);
  } catch {
    if (onCleanup) onCleanup();
    return NOOP;
  }

  const stop = async () => {
    if (stopped) return;
    stopped = true;
    try { if (rec.state !== 'inactive') rec.stop(); } catch { /* ignore */ }
    await new Promise((r) => { setTimeout(r, 80); });   // let the final ondataavailable fire
    await chain;                                         // drain the upload queue
    if (onCleanup) onCleanup();
    try {
      await stopRecording(companyId, sessionId, candidateId, kind, recordingId, {
        mime, started_at: startedAt, bytes, chunks,
      });
    } catch { /* ignore */ }
  };

  return { stop, recordingId, kind };
}

// Build one stream = screen video + (system audio ⊕ mic audio ⊕ AI voice) mixed via Web Audio.
// The mic may not be live yet when sharing starts, so we poll and connect it the moment it appears.
export function buildScreenAV(screenStream, getMicStream, getAiStream) {
  const videoTrack = screenStream.getVideoTracks()[0];
  const sysAudio = screenStream.getAudioTracks();
  const AC = window.AudioContext || window.webkitAudioContext;

  let ctx = null;
  let dest = null;
  let micTimer = null;
  let micSource = null;
  let micTrack = null;

  if (AC) {
    try {
      ctx = new AC();
      if (ctx.state === 'suspended') ctx.resume().catch(() => {});
      dest = ctx.createMediaStreamDestination();

      if (sysAudio.length) {
        try { ctx.createMediaStreamSource(new MediaStream(sysAudio)).connect(dest); } catch { /* ignore */ }
      }

      // AI voice bus (persistent silent track until Wiza speaks) → recorded at full volume.
      const aiStream = getAiStream && getAiStream();
      if (aiStream && aiStream.getAudioTracks().length) {
        try { ctx.createMediaStreamSource(aiStream).connect(dest); } catch { /* ignore */ }
      }

      const syncMic = () => {
        const mic = getMicStream && getMicStream();
        const nextTrack = mic?.getAudioTracks()[0];
        if (nextTrack === micTrack && nextTrack?.readyState === 'live') return;
        if (micSource) { try { micSource.disconnect(); } catch { /* ignore */ } }
        micSource = null;
        micTrack = null;
        if (nextTrack?.readyState === 'live') {
          try {
            micSource = ctx.createMediaStreamSource(mic);
            micSource.connect(dest);
            micTrack = nextTrack;
          } catch { /* retry when the stream is available */ }
        }
      };
      syncMic();
      micTimer = setInterval(syncMic, 500);
    } catch {
      ctx = null; dest = null;
    }
  }

  const tracks = [videoTrack];
  if (dest && dest.stream.getAudioTracks().length) tracks.push(dest.stream.getAudioTracks()[0]);
  else if (sysAudio.length) tracks.push(sysAudio[0]);   // fallback: at least system audio, unmixed

  const cleanup = () => {
    if (micTimer) { clearInterval(micTimer); micTimer = null; }
    if (micSource) { try { micSource.disconnect(); } catch { /* ignore */ } }
    try { if (ctx) ctx.close(); } catch { /* ignore */ }
  };

  return { combined: new MediaStream(tracks), cleanup };
}

// Mix mic + AI voice into one audio stream (for the audio-only recorder).
function buildAudioMix(micStream, getAiStream) {
  const AC = window.AudioContext || window.webkitAudioContext;
  const ai = getAiStream && getAiStream();
  if (!AC || !ai || !ai.getAudioTracks().length) return { combined: micStream, cleanup: () => {} };
  let ctx = null;
  let dest = null;
  try {
    ctx = new AC();
    if (ctx.state === 'suspended') ctx.resume().catch(() => {});
    dest = ctx.createMediaStreamDestination();
    if (micStream && micStream.getAudioTracks().length) {
      try { ctx.createMediaStreamSource(micStream).connect(dest); } catch { /* ignore */ }
    }
    try { ctx.createMediaStreamSource(ai).connect(dest); } catch { /* ignore */ }
  } catch { ctx = null; dest = null; }
  const combined = (dest && dest.stream.getAudioTracks().length) ? dest.stream : micStream;
  const cleanup = () => { try { if (ctx) ctx.close(); } catch { /* ignore */ } };
  return { combined, cleanup };
}

/**
 * A Web Audio "bus" for the AI voice (server TTS — Piper/ElevenLabs). `play(arrayBuffer, opts)` decodes
 * the audio and routes it into the recording bus, and — when `opts.toSpeakers` is set — ALSO to the
 * speakers so it's the live voice the candidate hears (one fetch/decode for both). `play` resolves when
 * playback ENDS, so callers can await it for turn-taking. `stop()` cuts any in-flight line. The bus
 * `stream` is mixed into the screen + audio recorders so Wiza's voice is in the file too. One bus is
 * created per experience (in MediaProvider) and reused for every line.
 */
export function createAiVoiceBus() {
  const AC = window.AudioContext || window.webkitAudioContext;
  if (!AC) return null;
  let ctx;
  let dest;
  try {
    ctx = new AC();
    dest = ctx.createMediaStreamDestination();
  } catch { return null; }
  let current = null;   // the source currently playing — so a teardown/end can stop it
  // Resolves when playback ENDS, returning the clip duration (seconds). `onStart(duration)` fires the
  // moment audio begins — used to drive a live caption reveal in sync with the voice.
  const play = async (arrayBuffer, { toSpeakers = false, onStart } = {}) => {
    if (!arrayBuffer || !arrayBuffer.byteLength) return 0;
    try { if (ctx.state === 'suspended') await ctx.resume(); } catch { /* ignore */ }
    let buf;
    try { buf = await ctx.decodeAudioData(arrayBuffer.slice(0)); } catch { return 0; }  // slice: decode detaches
    const duration = buf.duration || 0;
    await new Promise((resolve) => {
      try {
        const src = ctx.createBufferSource();
        src.buffer = buf;
        src.connect(dest);                                                      // → recording mix
        if (toSpeakers) { try { src.connect(ctx.destination); } catch { /* ignore */ } }  // → live speakers
        src.onended = () => { if (current === src) current = null; resolve(); };
        current = src;
        src.start();
        if (onStart) { try { onStart(duration); } catch { /* ignore */ } }
      } catch { resolve(); }
    });
    return duration;
  };
  const stop = () => {
    if (!current) return;
    try { current.onended = null; current.stop(); } catch { /* ignore */ }
    current = null;
  };
  return { stream: dest.stream, play, stop, close: () => { try { ctx.close(); } catch { /* ignore */ } } };
}

/** Generic single-stream recorder (camera, or mic-only audio). For audio, mixes in the AI voice. */
export function startChunkRecording({ stream, kind, ids, getAiStream, timesliceMs = 5000 }) {
  const mime = pickMime(kind === 'audio' ? MIME_AUDIO : MIME_VIDEO);
  if (kind === 'audio') {
    const { combined, cleanup } = buildAudioMix(stream, getAiStream);
    return record({ stream: combined, mime, kind, ids, timesliceMs, onCleanup: cleanup });
  }
  return record({ stream, mime, kind, ids, timesliceMs });
}

/** Screen recorder: entire screen + system audio + mic + AI voice, muxed into one file. */
export function startScreenRecording({ screenStream, getMicStream, getAiStream, ids, timesliceMs = 5000 }) {
  if (!screenStream || typeof MediaRecorder === 'undefined') return NOOP;
  const { combined, cleanup } = buildScreenAV(screenStream, getMicStream, getAiStream);
  const mime = pickMime(combined.getAudioTracks().length ? MIME_AV : MIME_VIDEO);
  return record({ stream: combined, mime, kind: 'screen', ids, timesliceMs, onCleanup: cleanup });
}
